/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Action,
  HStack,
  Icon,
  ICON_CARD_BULLETED_OUTLINE,
  Message, Popover, PopoverProps, Stack,
  Text,
} from '@doveit/bricks';
import { capitalizeWords, formatDate } from '@doveit/hammer';
import PopoverPreview from '../../../components/popover-preview/PopoverPreview';
import { Property, PublishedOn } from '../../../providers/api/dtos';
import PublishedOnAvatar from '../../components/published-on-avatar/PublishedOnAvatar';
import { aggregatorLabels, aggregatorPromotionLabels, enabledAggregatorPromotion } from '../../constants';
import * as styles from './ViewPublishedOnPopoverPreview.style';
import { AggregatorStatus, Status } from '../../../domain/types';
import EditPublishedOnPromotionAction from '../edit-published-on-promotion-action/EditPublishedOnPromotionAction';

export interface ViewPublishedOnPopoverPreviewProps extends React.AriaAttributes {
  property: Partial<Pick<Property, 'status'>>,
  publishedOn: PublishedOn,
  trigger: PopoverProps['trigger'],
  canViewError?: boolean,
  canViewPublishedAt?: boolean,
  canViewPromotion?: boolean,
  canVisit?: boolean,
  canEditPromotion?: boolean,
  onEdit?: (publishedOn: PublishedOn) => void,
}

export const PUBLISHED_ON_DEFAULT_ERROR_MESSAGE = 'È presente un errore nella pubblicazione';

const ViewPublishedOnPopoverPreview: React.FC<ViewPublishedOnPopoverPreviewProps> = ({
  property,
  publishedOn,
  trigger,
  canViewError = false,
  canViewPublishedAt = false,
  canViewPromotion = false,
  canVisit = false,
  canEditPromotion = false,
  onEdit,
  ...rest
}) => {
  const hasError = publishedOn.aggregatorStatus === AggregatorStatus.ERROR;
  const hasUrl = !!publishedOn.url;
  const hasPromotion = !!publishedOn.aggregatorPromotion;

  const goToAggregatorPage = React.useCallback(() => {
    window.open(publishedOn.url, '_blank');
  }, [publishedOn.url]);

  const showInfo = React.useMemo(
    () => (canViewError && hasError) || (canVisit && hasUrl) || (canViewPromotion && hasPromotion),
    [canViewError, canViewPromotion, canVisit, hasError, hasPromotion, hasUrl],
  );

  const showActionSlot = React.useMemo(
    () => ((canVisit && hasUrl) || (canEditPromotion && property.status === Status.LIVE && enabledAggregatorPromotion.includes(publishedOn.aggregator))),
    [canEditPromotion, canVisit, hasUrl, property.status, publishedOn.aggregator],
  );

  return (
    <Popover
      trigger={trigger}
      hasArrow
      {...rest}
    >
      <div css={styles.wrapper}>
        <PopoverPreview
          title={capitalizeWords(aggregatorLabels[publishedOn.aggregator])}
          details={(canViewPublishedAt && !!publishedOn.publishedAt) ? `Pubblicato il ${formatDate(new Date(publishedOn.publishedAt))}` : undefined}
          avatar={(
            <PublishedOnAvatar
              aggregator={publishedOn.aggregator}
              size="L"
            />
          )}
        >
          {showInfo && (
            <Stack gap={150}>
              {canViewError && hasError && (
                <Message
                  message={publishedOn.errorMessage || PUBLISHED_ON_DEFAULT_ERROR_MESSAGE}
                  type="critical"
                />
              )}

              {canViewPromotion && hasPromotion && (
                <HStack aria-label="Informazioni vetrina">
                  <Icon path={ICON_CARD_BULLETED_OUTLINE} />
                  <Text.Body>Annuncio <strong>{aggregatorPromotionLabels[publishedOn.aggregatorPromotion!]}</strong></Text.Body>
                </HStack>
              )}
            </Stack>
          )}
          {showActionSlot && (
            <HStack gap={75} wrap="nowrap">
              {canVisit && hasUrl && (
                <div style={{ flex: 1 }}>
                  <Action
                    size="S"
                    color="primary"
                    label="Visualizza"
                    aria-label="Visita la pubblicazione"
                    onClick={goToAggregatorPage}
                    width="100%"
                  />
                </div>
              )}
              {canEditPromotion && (
                <div style={{ flex: 1 }}>
                  <EditPublishedOnPromotionAction
                    property={property}
                    publishedOn={publishedOn}
                    onSuccess={onEdit}
                    width="100%"
                  />
                </div>
              )}
            </HStack>
          )}
        </PopoverPreview>
      </div>
    </Popover>
  );
};

export default ViewPublishedOnPopoverPreview;
